import {
  ComputedRef,
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  reactive,
  watch,
} from '@vue/composition-api';

import { useStore } from '@/hooks/useStore';
import { useRoute } from '@/hooks/useRoute';
import { useBoardEntryViewTracking }
  from '@/components/SettouMessageBoard/composables/useBoardEntryViewTracking';
import boardEntryApi from '@/apis/board_entry';
import { BoardEntry } from '@/models/apis/settouMessageBoard/settouMessageBoardResponse';
import { dtFormat } from '@/lib/dateHelper';
import { waitForUserAndMasters } from '@/lib/masterHelper';
import {
  SearchParams,
  convSearchParamDateFromTo,
} from '@/components/SettouMessageBoard/utils/index';
import { BoardEntryIndexParams } from '@/models/apis/settouMessageBoard/settouMessageBoardRequest';
import {
  SettouMessageBoardState,
  TabFilterState,
  HanState,
  BoardEntryListState,
  BoardEntryModalState,
  BoardEntryConv,
} from '@/models/settouMessageBoard';
import {
  johaisetsuRoleMap,
  BOARD_KYOKU_ROLE_BUREAU_OWNER_MAP,
} from '@/components/SettouMessageBoard/consts/settou_message_board';
import { normalizeKey } from '@/lib/johaisetsu/johaisetsuHelper';
import useBoardDisplayControl, { provideBoardDisplayControl }
  from '@/components/SettouMessageBoard/composables/useBoardDisplayControl';
import { provideBoardTabPager }
  from '@/components/SettouMessageBoard/composables/useBoardTabPager';
import { provideBoardEntryCrud }
  from '@/components/SettouMessageBoard/composables/useBoardEntryCrud';
import { provideBoardEntryPermissions }
  from '@/components/SettouMessageBoard/composables/useBoardEntryPermissions';
import { provideBoardCsvExport }
  from '@/components/SettouMessageBoard/composables/useBoardCsvExport';
import { providePhotoModal }
  from '@/components/SettouMessageBoard/composables/usePhotoModal';
import { createInjection } from '@/lib/createInjection';

export interface UseBoardCoreResult {
  state: SettouMessageBoardState;
  isSuperAdmin: ComputedRef<boolean>;
  paginatedBoardEntries: ComputedRef<BoardEntryConv[]>;
  onChangeBoardTab: () => void;
  dtFormat: typeof dtFormat;
  searchBoardEntries: (params: SearchParams) => Promise<void>;
}

const { provide, inject } = createInjection<UseBoardCoreResult>('settouMessageBoard/useBoardCore');

export function provideBoardCore(): void {
  provide(buildBoardCore());
}

export function useBoardCore(): UseBoardCoreResult {
  return inject();
}

// 掲示板のタブ・班マスタ・投稿一覧の取得と検索/自動再取得を担い、
// 責務別の composable (タブページャー・CRUD・権限判定・CSV出力・写真モーダル) を provide する。
function buildBoardCore(): UseBoardCoreResult {
  const tabState = reactive<TabFilterState>({
    boardTabs: [],
    currentTabId: 0,
    filteredTabMinIdx: 0,
    maxFilteredTabCount: 3,
    onResizeFunc: (): void => {},
  });
  const hanState = reactive<HanState>({
    hanMsts: [],
    hanChoices: [],
    hanChoicesMap: {},
  });
  const entryListState = reactive<BoardEntryListState>({
    boardEntries: [],
    pager: {
      currentPage: 1,
      itemsPerPage: 50,
    },
    isDownloading: false,
    autoSearchTimer: null,
    dataReloadedAt: null,
  });
  const modalState = reactive<BoardEntryModalState>({
    editingBoardEntry: null,
    showEditModal: false,
    showDeleteModal: false,
    deleteBody: '',
    shouldShowErrorModal: false,
    errorBody: '',
    destroyId: null,
  });
  const state: SettouMessageBoardState = {
    tab: tabState,
    han: hanState,
    entryList: entryListState,
    modal: modalState,
  };
  const store = useStore();
  const userState = store.state.user;
  const isSuperAdmin = computed<boolean>(() => {
    return userState.has_role_super_admin;
  });
  const { currentBoardTab, isCreatableRole, isRepliableRole, isUpperOrganization } = (() => {
    const injectedState = provideBoardDisplayControl(state.tab);
    return useBoardDisplayControl(injectedState);
  })();

  const paginatedBoardEntries = computed<BoardEntryConv[]>(() => {
    const nextPageIdx = state.entryList.pager.currentPage * state.entryList.pager.itemsPerPage;
    const minIdx = nextPageIdx - state.entryList.pager.itemsPerPage;
    return state.entryList.boardEntries.filter((e, i) => {
      return i >= minIdx && i < nextPageIdx;
    });
  });

  const { route } = useRoute();
  const viewTracking = useBoardEntryViewTracking({
    getBoardEntries: () => state.entryList.boardEntries,
    getPaginatedBoardEntries: () => paginatedBoardEntries.value,
    getItemsPerPage: () => state.entryList.pager.itemsPerPage,
    getCurrentBoardMasterId: () => state.tab.currentTabId,
    setCurrentPage: (page: number) => { state.entryList.pager.currentPage = page; },
    setCurrentBoardMasterId: (boardMasterId: number) => { state.tab.currentTabId = boardMasterId; },
  });
  // 注意: core の onMounted より先に provide すること
  // (prepareBoardTabs が参照する maxFilteredTabCount をタブページャーのリサイズ処理の初期化で確定させるため)。
  provideBoardTabPager({
    tabState: state.tab,
    armMarkNewest: viewTracking.armMarkNewest,
  });
  providePhotoModal();
  provideBoardEntryPermissions({
    isSuperAdmin,
    isCreatableRole,
    isRepliableRole,
    isUpperOrganization,
  });

  const applyNotificationQuery = (): void => {
    const boardMasterIdParam = route.value.query.board_master_id;
    const latestEntryIdParam = route.value.query.latest_entry_id;
    viewTracking.applyNotificationQuery(
      typeof boardMasterIdParam === 'string' ? boardMasterIdParam : null,
      typeof latestEntryIdParam === 'string' ? latestEntryIdParam : null,
    );
  };

  onMounted(async() => {
    await waitForUserAndMasters();
    await getBoardHanMasters();
    await prepareBoardTabs();
    applyNotificationQuery();
    state.entryList.autoSearchTimer = window.requestInterval(() => {
      reloadBoardEntries();
    }, 60000);
  });

  watch(() => state.entryList.pager.currentPage, async() => {
    await nextTick();
    viewTracking.setupEntryObserver();
  });

  const prepareBoardTabs = async(): Promise<void> => {
    const res = await boardEntryApi.getBoardTabs();
    state.tab.boardTabs = res.data;
    if (state.tab.boardTabs.length === 0) return;

    // 所属組織の掲示板をデフォルトで表示
    const normalizedJohaisetsuRole = normalizeKey(userState.johaisetsu_role);
    // co_shutokou_*ロールは自身の板ではなく、対応する局(t-west等)の板を所属組織の掲示板として扱う
    const ownBoardOwnerName = BOARD_KYOKU_ROLE_BUREAU_OWNER_MAP[normalizedJohaisetsuRole] ?? normalizedJohaisetsuRole;
    const ownCompanyBoardTabIdx = state.tab.boardTabs.findIndex(e => e.owner_company_name === ownBoardOwnerName);
    if (ownCompanyBoardTabIdx < 0) {
      state.tab.currentTabId = state.tab.boardTabs[0].id;
      return;
    }
    state.tab.currentTabId = state.tab.boardTabs[ownCompanyBoardTabIdx].id;
    if (ownCompanyBoardTabIdx < state.tab.maxFilteredTabCount) {
      return;
    }
    // 所属組織のタブが表示範囲外の場合、一番右側に表示されるように範囲を移動する
    state.tab.filteredTabMinIdx = ownCompanyBoardTabIdx - state.tab.maxFilteredTabCount + 1;
  };
  const getBoardHanMasters = async(): Promise<void> => {
    const res = await boardEntryApi.getBoardHanMasters();
    state.han.hanMsts = res.data;
  };
  const refreshHanChoices = (): void => {
    const boardTabJohaisetsuRole = johaisetsuRoleMap[currentBoardTab.value.owner_company_name];
    state.han.hanChoices = state.han.hanMsts.filter(e => e.johaisetsu_role === boardTabJohaisetsuRole);
    state.han.hanChoicesMap = {};
    for (const han of state.han.hanMsts) {
      state.han.hanChoicesMap[han.id] = han.name.toString();
    }
  };
  let latestSearchParams: SearchParams | null = null;
  const fetchBoardEntries = async(params: SearchParams): Promise<BoardEntryConv[] | null> => {
    refreshHanChoices();
    const [dateFrom, dateTo] = convSearchParamDateFromTo(params.dateFrom, params.dateTo);
    const requestedTabId = state.tab.currentTabId;
    const searchParams: BoardEntryIndexParams = {
      board_master_id: requestedTabId,
      date_from: dateFrom,
      date_to: dateTo,
      han: params.han ?? null,
      is_reported_to_honsha: params.reportToHonsha ?? null,
      company_name: params.companyName ?? null,
    };
    const res = await boardEntryApi.index(searchParams);
    state.entryList.dataReloadedAt = new Date();
    // 素早く板を切り替えた時に別の板の投稿が表示されてしまうのを防ぐ
    if (state.tab.currentTabId !== requestedTabId) return null;

    const converted = await convSettouBoardEntriesResponse(res.data);
    state.entryList.boardEntries = converted;
    await nextTick();
    viewTracking.setupEntryObserver();

    return converted;
  };
  const searchBoardEntries = async(params: SearchParams): Promise<void> => {
    latestSearchParams = params;
    state.entryList.pager.currentPage = 1;
    state.entryList.boardEntries = [];
    const entries = await fetchBoardEntries(params);
    if (entries === null) return;
    await viewTracking.runPendingViewAction();
  };
  const reloadBoardEntries = async(): Promise<void> => {
    if (!latestSearchParams) return;

    const savedPage = state.entryList.pager.currentPage;
    const savedScrollY = window.scrollY;
    const entries = await fetchBoardEntries(latestSearchParams);
    if (entries === null) return;
    const lastPage = Math.ceil(entries.length / state.entryList.pager.itemsPerPage) || 1;
    if (savedPage <= lastPage) {
      state.entryList.pager.currentPage = savedPage;
      await nextTick();
      window.scrollTo(0, savedScrollY);
    } else {
      state.entryList.pager.currentPage = lastPage;
      await nextTick();
      window.scrollTo(0, 0);
    }
  };
  const onChangeBoardTab = (): void => {
    viewTracking.armMarkNewest(state.tab.currentTabId); // タブ切替先の最新投稿を既読化する
  };
  const convSettouBoardEntriesResponse = async(entries: BoardEntry[]): Promise<BoardEntryConv[]> => {
    const converted = entries.map((e, i) => {
      return {
        ...e,
        dispNo: entries.length - i,
        hanDisp: (state.han.hanChoicesMap[e.han ?? 0] || ''),
        formattedContent: convertLinkInText(e.content),
        formattedReply: convertLinkInText(e.reply),
      };
    });

    const movePinnedToTop = (boardEntries: BoardEntryConv[]): BoardEntryConv[] => {
      const pinned = boardEntries.find(entry => entry.is_pinned);
      const others = boardEntries.filter(entry => !entry.is_pinned);
      return pinned ? [pinned, ...others] : others;
    };

    const sortedEntries = movePinnedToTop(converted);
    // 本社ユーザーは本社本部報告が必要かつ掲示の場合、投稿を表示する
    if (userState.johaisetsu_role === 'honsha') {
      return sortedEntries.filter(e => e.is_reported_to_honsha);
    }
    return sortedEntries;
  };
  const convertLinkInText = (text: string | null): string => {
    if (!text) return '';

    const regExpUrl = /\b(https?:\/\/[\S]+)\b/g;
    const regExpDrivePath = /\b([A-Za-z]:\\[\S]+)\b/g;
    // onclick属性値として埋め込むため、属性抜け出し防止のHTMLエスケープを行う
    const escapeHtml = (str: string): string => {
      return str
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    };
    const formatLink = (url: string, text: string) => {
      if (url.match(regExpUrl)) {
        return `<a href="${url}" target="_blank">${text}<i class="fa fa-external-link-alt ml3"></i></a>`;
      } else {
        // JS文字列リテラル用にバックスラッシュとクォートをエスケープした上で、属性値としてHTMLエスケープする
        const escapedPath = escapeHtml(url.replace(/\\/g, '\\\\').replace(/'/g, "\\'"));
        return `<a href="" onclick="
          if (!navigator.clipboard) { return false; }
          navigator.clipboard.writeText('${escapedPath}');
          return false;
        ">${text}<i class="far fa-copy ml3"></i></a>`;
      }
    };

    if (text.match('<a href')) {
      return text.replace(/<a href="([^"]+)">([^<]+)<\/a>/g, (match, url, text) => formatLink(url, text));
    } else if (text.match(regExpUrl) || text.match(regExpDrivePath)) {
      text = text.replace(/<p>|<\/p>/g, '').replace(/<br>/g, '\r\n');
      return text.replaceAll(regExpUrl, (url) => formatLink(url, url))
        .replaceAll(regExpDrivePath, (path) => formatLink(path, path));
    } else {
      return text;
    }
  };
  provideBoardEntryCrud({
    modalState: state.modal,
    getLatestSearchParams: () => latestSearchParams,
    searchBoardEntries,
    reloadBoardEntries,
  });
  provideBoardCsvExport({
    tabState: state.tab,
    entryListState: state.entryList,
    modalState: state.modal,
    getLatestSearchParams: () => latestSearchParams,
  });
  onUnmounted(() => {
    window.clearRequestInterval(state.entryList.autoSearchTimer);
    viewTracking.disconnectObserver();
  });

  return {
    state,
    isSuperAdmin,
    paginatedBoardEntries,
    onChangeBoardTab,
    dtFormat,
    searchBoardEntries,
  };
}
